/**
 * Outbound `resourceWrite` / `resourceMkdir` support for the browser relay.
 *
 * Kept out of `relay-connection-runtime.ts` so the failure taxonomy and URI
 * construction stay pure and unit-testable without standing up a transport.
 * The runtime keeps only the thin methods that own the live `AhpClient`.
 */

import {AhpErrorCodes, JsonRpcErrorCodes} from '@microsoft/agent-host-protocol'
import {RpcError} from '@microsoft/agent-host-protocol/client'

/**
 * Why a sandbox write could not be completed.
 *
 * `denied` and `alreadyExists` are deliberately distinct from `failed`: the
 * uploader must never auto-retry a denial, and `alreadyExists` is the signal
 * that drives collision renaming rather than an error the user should see.
 */
export type ResourceWriteFailureKind =
  | 'denied'
  | 'alreadyExists'
  | 'conflict'
  | 'parentMissing'
  | 'unsupported'
  | 'failed'

export class ResourceWriteError extends Error {
  readonly kind: ResourceWriteFailureKind

  constructor(kind: ResourceWriteFailureKind, message: string, options?: {cause?: unknown}) {
    super(message, options)
    this.name = 'ResourceWriteError'
    this.kind = kind
  }
}

/**
 * Whether the uploader may transparently retry after this failure. A denial or
 * an unsupported host will fail identically however many times we ask, and
 * retrying a conflict without re-resolving the etag would just lose the same
 * update again.
 */
export function isRetriableWriteFailure(error: unknown): boolean {
  return error instanceof ResourceWriteError && error.kind === 'failed'
}

export function toResourceWriteError(error: unknown): ResourceWriteError {
  if (error instanceof ResourceWriteError) return error

  if (error instanceof RpcError) {
    const kind = WRITE_FAILURE_BY_CODE[error.code]
    if (kind) return new ResourceWriteError(kind, error.message, {cause: error})
    return new ResourceWriteError('failed', error.message, {cause: error})
  }

  return new ResourceWriteError('failed', error instanceof Error ? error.message : String(error), {cause: error})
}

const WRITE_FAILURE_BY_CODE: Record<number, ResourceWriteFailureKind | undefined> = {
  [AhpErrorCodes.NotFound]: 'parentMissing',
  [AhpErrorCodes.PermissionDenied]: 'denied',
  [AhpErrorCodes.AlreadyExists]: 'alreadyExists',
  [AhpErrorCodes.Conflict]: 'conflict',
  // A host too old to dispatch `resourceWrite` answers MethodNotFound. Surfacing that
  // as `unsupported` lets callers fall back to inlining instead of showing a hard error.
  [JsonRpcErrorCodes.MethodNotFound]: 'unsupported',
}

/**
 * Joins a host directory path and already-sanitized path segments into a `file:` URI.
 *
 * `directory` comes from the host (`RelayHostInfo.defaultDirectory`) and may be a bare
 * POSIX path or an existing `file:` URI. Both forms are normalized through the URL
 * parser so an authority (`file://server/share`) survives and reserved characters in a
 * bare path (`/work/My #1`) are encoded rather than truncating the URI at a fragment.
 *
 * Segments are percent-encoded individually so a space or a `#` in a filename cannot
 * restructure the URI. Callers are still responsible for sanitizing segments first —
 * encoding a `..` would preserve it, not neutralize it.
 */
export function buildFileUri(directory: string, ...segments: string[]): string {
  const base = toFileUrl(directory)
  const encoded = segments
    .filter(segment => segment.length > 0)
    .map(segment => encodeURIComponent(segment))
    .join('/')

  const trimmed = base.pathname.endsWith('/') ? base.pathname.slice(0, -1) : base.pathname
  base.pathname = encoded.length > 0 ? `${trimmed}/${encoded}` : trimmed
  return base.href
}

/**
 * Accepts either an existing `file:` URI or a bare filesystem path and returns a URL.
 * A bare path is encoded segment-by-segment; passing it to `new URL` directly would
 * let `#` or `?` in a directory name silently become a fragment or query.
 */
function toFileUrl(directory: string): URL {
  // These are absolute `file:` URIs describing a path inside the sandbox, never a
  // page-relative URL, so resolving them against `window.location.origin` would be
  // wrong — and this code also runs during SSR where there is no location.
  // eslint-disable-next-line no-restricted-syntax -- absolute file: URI, not page-relative
  if (/^file:/i.test(directory)) return new URL(directory)
  const absolute = directory.startsWith('/') ? directory : `/${directory}`
  const encoded = absolute.split('/').map(encodeURIComponent).join('/')
  // eslint-disable-next-line no-restricted-syntax -- absolute file: URI, not page-relative
  return new URL(`file://${encoded}`)
}
